import React, { useEffect, useRef } from "react";
import pageLogo from "../assets/page_logo.png";

const APP_STORE_URL = "https://apps.apple.com/us/app/my-hoom/id6751858013";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.myhoom.myhoom";

/**
 * The store this device should be sent to, or "" when we should not guess.
 *
 * Lifted out of the effect so it can also be consulted during render — see below.
 */
function storeForUserAgent() {
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  return isIOS ? APP_STORE_URL : isAndroid ? PLAY_STORE_URL : "";
}

/**
 * Desktop / unknown-device fallback for the QR smart redirect.
 *
 * Not linked from any nav or footer. Two entry points reach it, both in
 * components/readyToSend.js: the home page QR code and the "Download the
 * App" anchor beside it. Both are full page loads, so the inline script
 * in public/index.html sends iOS and Android to their store before the
 * React bundle downloads and this markup never renders for them.
 *
 * The useEffect below covers client-side navigation, where that script
 * does not re-run. Nothing routes here that way today — it is the net
 * under a future <Link>, and under the script failing to run at all.
 *
 * Canonical address: https://www.myhoom.com/app_redirect
 * Specified in docs/spec/app-download.md.
 */
function AppRedirect() {
  // One navigation per destination. React.StrictMode (src/index.js:11) invokes effects twice in
  // development, so without this the app fires `replace` twice for the same URL and the second
  // call aborts the first — a bare ERR_ABORTED rather than a wrong destination. Same guard, and
  // the same reason, as components/stagingQrRedirect.js.
  const redirectedTo = useRef(null);

  // Decided during render, not inside the effect. Arriving here by client-side navigation, the
  // effect runs only after React has committed this render — so returning the desktop fallback
  // markup unconditionally is precisely the flash a phone user sees before the store opens.
  // Knowing the target up here lets us render nothing instead [AC-APPDL-F7].
  const target = storeForUserAgent();

  useEffect(() => {
    if (!target || redirectedTo.current === target) return;
    redirectedTo.current = target;
    // replace(), not assign(): a pushed entry would sit under the store, so Back would land here
    // and redirect forward again, trapping the visitor between the two.
    window.location.replace(target);
  }, [target]);

  // A device we are about to redirect gets a blank frame, never the "pick a store" page it is not
  // meant to see. Desktop and anything unrecognised fall through to the markup below [AC-APPDL-E1].
  if (target) return null;

  return (
    <div
      style={{
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "48px 24px",
        background: "#FFFDF7",
      }}
    >
      <img
        src={pageLogo}
        alt="my HOOM"
        style={{ width: "180px", maxWidth: "60%", marginBottom: "8px" }}
      />
      <h1 style={{ color: "#2E7D32", fontSize: "1.6rem", margin: "0 0 6px" }}>
        Get the my HOOM app
      </h1>
      <p
        style={{
          color: "#F7941D",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontSize: "0.85rem",
          margin: "0 0 32px",
        }}
      >
        Have one on me
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <a href={APP_STORE_URL} style={buttonStyle}>
          <span style={smallLabelStyle}>Download on the</span>
          <span style={storeNameStyle}>App Store</span>
        </a>
        <a href={PLAY_STORE_URL} style={buttonStyle}>
          <span style={smallLabelStyle}>Get it on</span>
          <span style={storeNameStyle}>Google Play</span>
        </a>
      </div>
      <p style={{ marginTop: "28px", fontSize: "0.85rem", color: "#8a7a6d" }}>
        Scan the QR code again on your phone, or tap a store above.
      </p>
    </div>
  );
}

const buttonStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "260px",
  padding: "14px 20px",
  background: "#1a1a1a",
  color: "#ffffff",
  textDecoration: "none",
  borderRadius: "12px",
  lineHeight: 1.3,
};

const smallLabelStyle = {
  fontSize: "0.7rem",
  fontWeight: 400,
  opacity: 0.8,
};

const storeNameStyle = {
  fontSize: "1.05rem",
  fontWeight: 600,
};

export default AppRedirect;
